// TIME. Two clocks, and confusing them is the bug this file exists to make impossible.
//
//   CHAIN TIME (`asOf.second`) — what every rendered age, freshness and "how old is this token" is measured
//   against. Solana block time trails wall clock by a variable few seconds, and with ingestion behind it has
//   trailed by minutes; a wall-clock window once held NO ROWS FOR ANY TOKEN, and the screen read as a dead
//   market rather than a stale pipeline.
//
//   WALL CLOCK (`Date.now()`) — legal in this file ONLY, and legal to CALL for exactly two kinds of thing,
//   neither of which is data: a TRANSPORT measurement (the socket watchdog — has any frame arrived in 20 s?)
//   and a CREDENTIAL DEADLINE (the bearer's absolute expiry; the order vault's 24 h vendor authorisation).
//   A credential's clock is wall-clock by construction — it was issued against one — and comparing it to
//   chain time would expire a session by however far block time happens to be trailing.
//   `tests/structure.test.ts` asserts `Date.now()` appears nowhere else in `src/`.

/** Wall-clock milliseconds. THE ONLY `Date.now()` in the app, and callable only for the two cases above.
 *  Anything about DATA freshness uses `asOf.second` and the helpers below. */
export function nowMs(): number {
  return Date.now();
}

/** Elapsed time, compactly, between a chain-time instant and a chain-time NOW.
 *
 *  `nowSec` is `asOf.second` — the caller passes it, so this function has no clock of its own and cannot
 *  accidentally acquire a wall-clock one. It rolls over into months and years because it renders TOKEN AGE as
 *  well as trade timestamps, and "1583d" is not something anyone reads as "four years".
 *
 *  Under ~5 s it reads "just now" rather than "0s": at the frontier the difference is one flush period, and
 *  "0s" invites the reading that the clock is broken. */
export function ago(unixSec: number, nowSec: number): string {
  const s = Math.max(0, Math.floor(nowSec) - Math.floor(unixSec));
  if (s < 5) return "just now";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  const d = Math.floor(s / 86400);
  if (d < 30) return `${d}d`;
  if (d < 365) return `${Math.floor(d / 30)}mo`;
  const y = d / 365;
  return y < 10 ? `${y.toFixed(1)}y` : `${Math.floor(y)}y`;
}

/**
 * Token age from the creation instant the API serves, against chain-time now.
 *
 * ⚠️ THE ARGUMENT IS UNIX **SECONDS**, and it took this parameter being named `createdAtMs` to make that
 * wrong. The API's stated unit is unix seconds in chain time on EVERY field, `TokenRow.createdAt` included;
 * this divided it by 1,000 again, so a token created ten minutes ago was measured from 1970-01-21 and every
 * dated row on the board rendered "56.5y". Both halves of that failure are here — the ones we could not date
 * printed "—" and the ones we could printed a number that was 56 years wrong — and the second is the worse
 * one, because an em dash reads as missing while "56y" reads as a fact.
 *
 * `null` → "—", NEVER "0s". An unknown age must not render as a brand-new token: that is the exact misreading
 * that makes an impersonation signal dangerous — an impersonator we happen to know nothing about would look
 * identical to one minted minutes ago. `nowSec` null (no head yet) is also "—" rather than a guess.
 *
 * A NON-POSITIVE INSTANT IS AN ABSENCE, not a date in 1970. Zero is what an unset timestamp serialises to
 * anywhere in the chain of systems behind this number, and rendering it as an age is how a missing value
 * comes to look like the oldest token on the board.
 */
export function age(createdAtSec: number | null | undefined, nowSec: number | null): string {
  if (createdAtSec == null || nowSec == null || !(createdAtSec > 0)) return "—";
  return ago(createdAtSec, nowSec);
}

/**
 * WHAT THE AGE IS AN AGE OF — one sentence per source, spelled once for every screen that renders one.
 *
 * The distinction survived the vendor fallback landing rather than being erased by it: an age we watched
 * happen and an age an indexer reported are different claims, and a token page and a board row must not
 * describe the same number two different ways. `null` is the only case that is about US rather than about the
 * token, and it says so.
 */
export function ageTitle(source: "genesis" | "vendor" | null): string {
  if (source === "genesis") return "we watched this token be born";
  if (source === "vendor")
    return "creation date from a free token list — an upper bound, not a launch we saw";
  return "no creation date for this token";
}
